/**
 * Editing operations on a level's wall graph.
 *
 * Every function here returns a new Level rather than mutating one, so the op
 * log can keep previous states for undo without defensive copying.
 *
 * The invariants these maintain, and the reason this file exists at all:
 *
 *  - Walls share junctions. Dragging a corner moves every wall meeting there,
 *    because they are the same point, not three points that happen to coincide.
 *  - Openings are positioned along their wall, not in world space, so they
 *    travel with it and survive a split.
 *  - An opening never hangs off the end of its wall. Anything that shortens a
 *    wall re-clamps them.
 */

import { closestPointOnSegment, distance, round } from './geometry';
import {
  DEFAULT_WALL_THICKNESS,
  OPENING_DEFAULTS,
  type Junction,
  type Level,
  type Opening,
  type OpeningType,
  type Point,
  type RoomMeta,
  type Wall,
} from './types';

let idCounter = 0;

/** Ids only need to be unique within a document, not globally meaningful. */
export function newId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${idCounter.toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

/** Test helper: makes id sequences repeatable across runs. */
export function resetIdCounter(): void {
  idCounter = 0;
}

export function junctionById(level: Level, id: string): Junction | undefined {
  return level.junctions.find((junction) => junction.id === id);
}

export function wallById(level: Level, id: string): Wall | undefined {
  return level.walls.find((wall) => wall.id === id);
}

/** Endpoints of a wall, or null when either junction is missing. */
export function wallEnds(level: Level, wall: Wall): { a: Point; b: Point } | null {
  const a = junctionById(level, wall.a);
  const b = junctionById(level, wall.b);
  if (!a || !b) return null;
  return { a: { x: a.x, y: a.y }, b: { x: b.x, y: b.y } };
}

export function wallLength(level: Level, wall: Wall): number {
  const ends = wallEnds(level, wall);
  return ends ? distance(ends.a, ends.b) : 0;
}

/** Clamps an opening so it stays within a wall of the given length. */
function clampOpening(opening: Opening, lengthMm: number): Opening {
  const width = Math.min(opening.width, Math.max(lengthMm, 0));
  const offset = Math.min(Math.max(opening.offset, 0), Math.max(lengthMm - width, 0));
  return { ...opening, width, offset };
}

/** Re-clamps every opening on the given walls. Call after changing geometry. */
function reclampOpenings(level: Level, wallIds: Set<string>): Level {
  if (wallIds.size === 0) return level;
  const lengths = new Map<string, number>();
  for (const wall of level.walls) {
    if (wallIds.has(wall.id)) lengths.set(wall.id, wallLength(level, wall));
  }
  return {
    ...level,
    openings: level.openings.map((opening) => {
      const lengthMm = lengths.get(opening.wall);
      return lengthMm === undefined ? opening : clampOpening(opening, lengthMm);
    }),
  };
}

/** Drops junctions no wall references any more. */
function pruneJunctions(level: Level): Level {
  const used = new Set<string>();
  for (const wall of level.walls) {
    used.add(wall.a);
    used.add(wall.b);
  }
  return { ...level, junctions: level.junctions.filter((j) => used.has(j.id)) };
}

export function addJunction(level: Level, point: Point): { level: Level; junctionId: string } {
  const junction: Junction = { id: newId('j'), ...round(point) };
  return { level: { ...level, junctions: [...level.junctions, junction] }, junctionId: junction.id };
}

/**
 * Splits a wall at a point on it, returning the id of the new junction.
 *
 * Openings move to whichever half now contains them. One that straddles the cut
 * would have to be truncated either way, so it stays on the half holding more
 * of it, clamped to fit.
 */
export function splitWall(
  level: Level,
  wallId: string,
  at: Point,
): { level: Level; junctionId: string } | null {
  const wall = wallById(level, wallId);
  if (!wall) return null;
  const ends = wallEnds(level, wall);
  if (!ends) return null;

  const { point, t } = closestPointOnSegment(at, ends.a, ends.b);
  const totalLength = distance(ends.a, ends.b);
  const firstLength = totalLength * t;

  // Splitting at the very end would leave a zero-length wall.
  if (firstLength < 1 || totalLength - firstLength < 1) return null;

  const junction: Junction = { id: newId('j'), ...round(point) };
  const first: Wall = { ...wall, id: newId('w'), a: wall.a, b: junction.id };
  const second: Wall = { ...wall, id: newId('w'), a: junction.id, b: wall.b };

  const openings = level.openings.map((opening) => {
    if (opening.wall !== wallId) return opening;
    const centre = opening.offset + opening.width / 2;
    return centre <= firstLength
      ? clampOpening({ ...opening, wall: first.id }, firstLength)
      : clampOpening(
          { ...opening, wall: second.id, offset: opening.offset - firstLength },
          totalLength - firstLength,
        );
  });

  return {
    level: {
      ...level,
      junctions: [...level.junctions, junction],
      walls: [...level.walls.filter((w) => w.id !== wallId), first, second],
      openings,
    },
    junctionId: junction.id,
  };
}

/**
 * Turns a clicked point into a junction to build from.
 *
 * Reusing a nearby junction, and splitting a wall the click landed on, is what
 * turns a sequence of strokes into a connected graph -- and a connected graph is
 * what makes rooms appear. Without the wall-splitting case a wall drawn against
 * the middle of another would merely cross it, leaving the "room" open.
 */
export function resolvePoint(
  level: Level,
  point: Point,
  snapMm: number,
): { level: Level; junctionId: string } {
  let nearest: { id: string; distance: number } | null = null;
  for (const junction of level.junctions) {
    const d = distance(point, junction);
    if (d <= snapMm && (!nearest || d < nearest.distance)) {
      nearest = { id: junction.id, distance: d };
    }
  }
  if (nearest) return { level, junctionId: nearest.id };

  let onWall: { wallId: string; point: Point; distance: number } | null = null;
  for (const wall of level.walls) {
    const ends = wallEnds(level, wall);
    if (!ends) continue;
    const hit = closestPointOnSegment(point, ends.a, ends.b);
    if (hit.distance <= snapMm && (!onWall || hit.distance < onWall.distance)) {
      onWall = { wallId: wall.id, point: hit.point, distance: hit.distance };
    }
  }
  if (onWall) {
    const split = splitWall(level, onWall.wallId, onWall.point);
    if (split) return split;
  }

  return addJunction(level, point);
}

/**
 * Adds a wall between two junctions. Returns the level unchanged if they are
 * the same junction or already directly connected.
 */
export function addWall(
  level: Level,
  fromJunctionId: string,
  toJunctionId: string,
  thickness = DEFAULT_WALL_THICKNESS,
): { level: Level; wallId: string | null } {
  if (fromJunctionId === toJunctionId) return { level, wallId: null };

  const duplicate = level.walls.find(
    (wall) =>
      (wall.a === fromJunctionId && wall.b === toJunctionId) ||
      (wall.a === toJunctionId && wall.b === fromJunctionId),
  );
  if (duplicate) return { level, wallId: duplicate.id };

  const wall: Wall = {
    id: newId('w'),
    a: fromJunctionId,
    b: toJunctionId,
    thickness,
    loadBearing: false,
  };
  return { level: { ...level, walls: [...level.walls, wall] }, wallId: wall.id };
}

/**
 * Draws a wall between two points, resolving each end onto existing geometry
 * first. This is the operation the wall tool performs.
 */
export function drawWall(
  level: Level,
  from: Point,
  to: Point,
  thickness: number,
  snapMm: number,
): Level {
  if (distance(from, to) < 1) return level;

  const start = resolvePoint(level, from, snapMm);
  const end = resolvePoint(start.level, to, snapMm);
  return addWall(end.level, start.junctionId, end.junctionId, thickness).level;
}

export function moveJunction(level: Level, junctionId: string, to: Point): Level {
  const moved: Level = {
    ...level,
    junctions: level.junctions.map((junction) =>
      junction.id === junctionId ? { ...junction, ...round(to) } : junction,
    ),
  };
  const touched = new Set(
    moved.walls.filter((w) => w.a === junctionId || w.b === junctionId).map((w) => w.id),
  );
  return reclampOpenings(moved, touched);
}

export function deleteWall(level: Level, wallId: string): Level {
  const without: Level = {
    ...level,
    walls: level.walls.filter((wall) => wall.id !== wallId),
    openings: level.openings.filter((opening) => opening.wall !== wallId),
  };
  return pruneJunctions(without);
}

export function setWallThickness(level: Level, wallId: string, thickness: number): Level {
  return {
    ...level,
    walls: level.walls.map((wall) =>
      wall.id === wallId ? { ...wall, thickness: Math.max(10, Math.round(thickness)) } : wall,
    ),
  };
}

export function setWallLoadBearing(level: Level, wallId: string, loadBearing: boolean): Level {
  return {
    ...level,
    walls: level.walls.map((wall) => (wall.id === wallId ? { ...wall, loadBearing } : wall)),
  };
}

/**
 * Places an opening on a wall, centred on a point along it.
 *
 * Returns the level unchanged when the wall is too short for the opening or the
 * spot is already taken -- two overlapping doors are not something to render and
 * silently produce.
 */
export function addOpening(
  level: Level,
  wallId: string,
  at: Point,
  type: OpeningType,
): { level: Level; openingId: string | null } {
  const wall = wallById(level, wallId);
  if (!wall) return { level, openingId: null };
  const ends = wallEnds(level, wall);
  if (!ends) return { level, openingId: null };

  const defaults = OPENING_DEFAULTS[type];
  const totalLength = distance(ends.a, ends.b);
  if (totalLength < defaults.width) return { level, openingId: null };

  const { t } = closestPointOnSegment(at, ends.a, ends.b);
  const centre = totalLength * t;
  const offset = Math.min(
    Math.max(centre - defaults.width / 2, 0),
    totalLength - defaults.width,
  );

  const overlaps = level.openings.some(
    (existing) =>
      existing.wall === wallId &&
      offset < existing.offset + existing.width &&
      existing.offset < offset + defaults.width,
  );
  if (overlaps) return { level, openingId: null };

  const opening: Opening = {
    id: newId('o'),
    wall: wallId,
    offset: Math.round(offset),
    width: defaults.width,
    sill: defaults.sill,
    head: defaults.head,
    type,
    ...(type === 'door' ? { swing: 'left-in' as const } : {}),
  };
  return { level: { ...level, openings: [...level.openings, opening] }, openingId: opening.id };
}

export function updateOpening(level: Level, openingId: string, patch: Partial<Opening>): Level {
  return {
    ...level,
    openings: level.openings.map((opening) => {
      if (opening.id !== openingId) return opening;
      const merged = { ...opening, ...patch };
      const wall = wallById(level, merged.wall);
      return wall ? clampOpening(merged, wallLength(level, wall)) : merged;
    }),
  };
}

export function deleteOpening(level: Level, openingId: string): Level {
  return { ...level, openings: level.openings.filter((opening) => opening.id !== openingId) };
}

/** World-space start and end of an opening along its wall. */
export function openingEnds(level: Level, opening: Opening): { a: Point; b: Point } | null {
  const wall = wallById(level, opening.wall);
  if (!wall) return null;
  const ends = wallEnds(level, wall);
  if (!ends) return null;

  const totalLength = distance(ends.a, ends.b);
  if (totalLength < 1) return null;

  const direction = {
    x: (ends.b.x - ends.a.x) / totalLength,
    y: (ends.b.y - ends.a.y) / totalLength,
  };
  return {
    a: {
      x: ends.a.x + direction.x * opening.offset,
      y: ends.a.y + direction.y * opening.offset,
    },
    b: {
      x: ends.a.x + direction.x * (opening.offset + opening.width),
      y: ends.a.y + direction.y * (opening.offset + opening.width),
    },
  };
}

/** Replaces or inserts a room's metadata, keyed by its id. */
export function upsertRoomMeta(level: Level, meta: RoomMeta): Level {
  const exists = level.rooms.some((room) => room.id === meta.id);
  return {
    ...level,
    rooms: exists
      ? level.rooms.map((room) => (room.id === meta.id ? meta : room))
      : [...level.rooms, meta],
  };
}

export function removeRoomMeta(level: Level, roomId: string): Level {
  return { ...level, rooms: level.rooms.filter((room) => room.id !== roomId) };
}
